import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import { NodeTypes } from "../constants";
import { TextareaAutosize } from "@mui/material";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useTheme } from "../../../contexts/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export const ListEditModal = ({ open, handleClose, data: listData }) => {
  const { darkMode } = useTheme();
  const { data } = listData || {};

  const isEdit = !!listData?.id;

  const [list, setList] = useState(data?.list || []);
  const [description, setDescription] = useState(data?.description || "");
  const [buttonName, setButtonName] = useState(data?.action?.name || "");

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      background: {
        default: darkMode ? "#121212" : "#ffffff",
        paper: darkMode ? "#121212" : "#ffffff",
      },
      text: {
        primary: darkMode ? "#fafafa" : "#000000",
        secondary: darkMode ? "#fafafa" : "#666666",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          contained: {
            backgroundColor: darkMode ? "#0086ff" : "#1976d2",
            "&:hover": {
              backgroundColor: darkMode ? "#0077ff" : "#1565c0",
            },
            color: "#ffffff",
          },
          outlined: {
            borderColor: darkMode ? "#2f2f2f" : "#1976d2",
            color: darkMode ? "#fafafa" : "#1976d2",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              color: darkMode ? "#fafafa" : "inherit",
              "& fieldset": {
                borderColor: darkMode ? "#2f2f2f" : "rgba(0, 0, 0, 0.23)",
              },
              "&:hover fieldset": {
                borderColor: darkMode ? "#404040" : "rgba(0, 0, 0, 0.87)",
              },
              "&.Mui-focused fieldset": {
                borderColor: darkMode ? "#0086ff" : "#1976d2",
              },
            },
            "& .MuiInputLabel-root": {
              color: darkMode ? "#fafafa" : "rgba(0, 0, 0, 0.6)",
              "&.Mui-focused": {
                color: darkMode ? "#0086ff" : "#1976d2",
              },
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: darkMode ? "#2f2f2f" : "rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },
  });

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: darkMode ? "#121212" : "background.paper",
    border: `1px solid ${darkMode ? "#2f2f2f" : "#cecece"}`,
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  const inputStyle = {
    marginRight: 12,
    width: 250,
  };

  const textareaStyle = {
    ...inputStyle,
    border: `1px solid ${darkMode ? "#2f2f2f" : "#cecece"}`,
    borderRadius: 4,
    width: 300,
    backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
    color: darkMode ? "#fafafa" : "#000000",
  };

  console.log("listData", list);

  const onAdd = () => {
    if (list.length && description && buttonName) {
      handleClose(
        NodeTypes.List,
        {
          description,
          action: {
            type: "button",
            name: buttonName,
          },
          list,
        },
        isEdit
      );
    } else {
      alert("Please fill all the details");
    }
  };

  const onClose = () => {
    handleClose(NodeTypes.List, null, isEdit);
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal open={open} onClose={onClose}>
        <Box sx={style}>
          <h1
            className={`font-bold text-2xl mb-4 ${
              darkMode ? "text-sky-400" : "text-sky-600"
            }`}
          >
            List
          </h1>
          <TextareaAutosize
            minRows={3}
            className="p-2"
            value={description}
            style={textareaStyle}
            placeholder="Write message here..."
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            label={"Action Button name"}
            variant="outlined"
            style={inputStyle}
            placeholder="Action Button name"
            value={buttonName}
            onChange={(e) => setButtonName(e.target.value)}
          />
          <Divider style={{ margin: 24 }} />
          <div className="mt-3">
            <div className={`${darkMode ? "text-gray-100" : "text-gray-900"}`}>
              Total List Items: {list.length}
            </div>
            {!list?.length && (
              <Button
                variant="contained"
                onClick={() => {
                  setList([{ name: "", description: "" }]);
                }}
              >
                Create list items
              </Button>
            )}
            {(list || []).map((item, index) => (
              <div key={index} className="flex m-2 items-center ">
                <div
                  className={`mr-3 ${
                    darkMode ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  {index + 1}.
                </div>
                <TextField
                  label={"Name"}
                  variant="outlined"
                  placeholder={"Name"}
                  style={inputStyle}
                  value={item.name}
                  onChange={(e) => {
                    const newList = [...list];
                    newList[index].name = e.target.value;
                    setList(newList);
                  }}
                />
                <TextField
                  width={400}
                  variant="outlined"
                  label={"Description"}
                  style={inputStyle}
                  placeholder={"Description"}
                  value={item.description}
                  onChange={(e) => {
                    const newList = [...list];
                    newList[index].description = e.target.value;
                    setList(newList);
                  }}
                />
                {index === list.length - 1 && (
                  <ControlPointIcon
                    style={{
                      cursor: "pointer",
                      color: darkMode ? "#fafafa" : "#000000",
                    }}
                    onClick={() => {
                      setList([...list, { name: "", description: "" }]);
                    }}
                  />
                )}
                {index !== list.length - 1 && index !== 0 && (
                  <HighlightOffIcon
                    style={{
                      cursor: "pointer",
                      color: darkMode ? "#fafafa" : "#000000",
                    }}
                    onClick={() => {
                      const newList = [...list];
                      newList.splice(index, 1);
                      setList(newList);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <Divider style={{ margin: 24 }} />
          <div className="mt-4">
            <Button variant="contained" onClick={onAdd}>
              {isEdit ? "Update" : "Add"}
            </Button>
            <Button
              style={{ marginLeft: 12 }}
              variant="outlined"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export const ListNode = (props) => {
  const { darkMode } = useTheme();
  const { data, selected, isAddVisible, onAdd, onEdit, ...rest } = props;
  return (
    <div
      className={`shadow-md rounded-md ${
        darkMode
          ? "bg-[#1e1e1e] border border-[#333333]"
          : "bg-white border-gray-200"
      } ${
        selected
          ? `border-solid border-2 ${
              darkMode ? "border-indigo-400" : "border-indigo-500/100"
            }`
          : ""
      } `}
    >
      <div className="flex flex-col">
        <div className="max-h-max px-2 py-1 text-left text-black text-xs font-bold rounded-t-md bg-teal-300">
          List
          <Tooltip title={rest.id}>
            <ErrorOutlineIcon style={{ fontSize: 10 }} />
          </Tooltip>
          <Tooltip className="ml-2" title="Edit">
            <EditIcon style={{ fontSize: 12 }} onClick={() => onEdit(props)} />
          </Tooltip>
        </div>
        <div
          className={`px-3 py-2 ${
            darkMode ? "text-gray-100" : "text-gray-900"
          }`}
        >
          <div className="text-xs">
            Description: <b>{data?.description}</b>
          </div>
          <div className="text-xs py-2">
            Action Name: <b>{data?.action?.name}</b>
          </div>
          <Divider style={{ margin: 6 }} />
          <div>Total List items: {data?.list?.length}</div>
          <div className="py-2 text-xs">
            {data?.list?.map((item, index) => {
              return (
                <div key={index} className="m-2 flex">
                  <div className={"mr-2"}>{index + 1} .</div>
                  <div className={"mr-2"}>
                    Name: <b>{item.name}</b>
                  </div>
                  <div>
                    Description: <b>{item.description}</b>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Handle id="b" type="target" position={Position.Left} />
        <HandleDot
          // addVisible={isAddVisible(rest)}
          addVisible={true}
          id="b"
          type="source"
          position={Position.Right}
          onAdd={(v) => onAdd(v, rest)}
        />
      </div>
    </div>
  );
};
